import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import {
  Plus, Trash, ArrowUp, ArrowDown, TextT, Image as ImageIcon,
  CursorClick, Minus, FloppyDisk, ArrowLeft, Eye,
} from "@phosphor-icons/react";

const BG_PRESETS = [
  "#ffffff", "#0f172a", "#0ea5e9", "#10b981", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899", "#facc15",
];

const WIDTH_LABELS = { sm: "Narrow", md: "Medium", lg: "Wide", full: "Full width" };

function newBlock(type) {
  const id = crypto.randomUUID();
  const defaults = {
    heading: { content: "Your headline", size: "xl", align: "center" },
    text:    { content: "A short supporting paragraph — describe your offer, story, or call to action.", size: "md", align: "center" },
    image:   { image_url: "", align: "center" },
    button:  { content: "Learn more", link: "https://", align: "center", size: "md" },
    divider: { align: "center" },
  }[type] || {};
  return { id, type, content: "", link: "", image_url: "", align: "center", size: "md", ...defaults, clicks: 0 };
}

export default function SplashEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("Untitled splash page");
  const [background, setBackground] = useState("#0ea5e9");
  const [textColor, setTextColor] = useState("#ffffff");
  const [maxWidth, setMaxWidth] = useState("md");
  const [blocks, setBlocks] = useState([
    newBlock("heading"),
    newBlock("text"),
    newBlock("button"),
  ]);
  const [slug, setSlug] = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/splash/pages/${id}`);
        setTitle(data.title || "");
        setBackground(data.background || "#ffffff");
        setTextColor(data.text_color || "#0f172a");
        setMaxWidth(data.max_width || "md");
        setBlocks((data.blocks || []).map((b) => ({ ...b })));
        setSlug(data.slug);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message);
        nav("/splash");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, nav]);

  const patch = (blockId, patchObj) =>
    setBlocks((prev) => prev.map((b) => (b.id === blockId ? { ...b, ...patchObj } : b)));

  const removeBlock = (blockId) => setBlocks((prev) => prev.filter((b) => b.id !== blockId));

  const move = (blockId, delta) =>
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === blockId);
      if (idx < 0) return prev;
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      const [item] = next.splice(idx, 1);
      next.splice(target, 0, item);
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title, background, text_color: textColor, max_width: maxWidth, blocks,
      };
      if (isEdit) {
        await api.patch(`/splash/pages/${id}`, payload);
        toast.success("Splash page saved");
      } else {
        const { data } = await api.post("/splash/pages", payload);
        toast.success(`Splash page created — /s/${data.slug}`);
        nav(`/splash/${data.id}/edit`, { replace: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const widthClass = useMemo(() => ({
    sm: "max-w-md",
    md: "max-w-2xl",
    lg: "max-w-4xl",
    full: "max-w-full",
  }[maxWidth] || "max-w-2xl"), [maxWidth]);

  if (loading) return <DashboardLayout><div className="text-slate-400">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <Link data-testid="splash-editor-back" to="/splash" className="shrink-0 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 dark:hover:text-white">
              <ArrowLeft size={14} weight="bold" /> Back
            </Link>
            <input
              data-testid="splash-editor-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled splash page"
              className="min-w-0 flex-1 rounded-xl bg-transparent border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {slug && (
              <a data-testid="splash-editor-view" href={`/s/${slug}`} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50">
                <Eye size={14} weight="bold" /> View
              </a>
            )}
            <button
              data-testid="splash-editor-save"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-1.5 text-xs font-black tracking-widest hover:bg-slate-800 disabled:opacity-60"
            >
              <FloppyDisk size={14} weight="bold" /> {saving ? "SAVING…" : "SAVE"}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[320px,1fr] gap-6">
          {/* Sidebar controls */}
          <aside className="space-y-4">
            <Panel title="Page style">
              <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Background</label>
              <div className="grid grid-cols-9 gap-1.5 mt-1.5">
                {BG_PRESETS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setBackground(c)}
                    aria-label={`Background ${c}`}
                    className={`h-7 rounded-md border-2 ${background === c ? "ring-2 ring-blue-500 border-white" : "border-white/60"}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
              <input
                type="color"
                value={/^#/.test(background) ? background : "#ffffff"}
                onChange={(e) => setBackground(e.target.value)}
                className="mt-2 w-full h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent"
              />

              <label className="mt-3 text-[11px] font-bold uppercase tracking-widest text-slate-500 block">Text color</label>
              <input
                type="color"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                className="mt-1 w-full h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent"
              />

              <label className="mt-3 text-[11px] font-bold uppercase tracking-widest text-slate-500 block">Layout width</label>
              <select
                value={maxWidth}
                onChange={(e) => setMaxWidth(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100"
              >
                {Object.entries(WIDTH_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Panel>

            <Panel title="Add block">
              <div className="grid grid-cols-2 gap-2">
                <AddBtn testid="add-heading" icon={TextT} label="Heading" onClick={() => setBlocks((p) => [...p, newBlock("heading")])} />
                <AddBtn testid="add-text" icon={TextT} label="Text" onClick={() => setBlocks((p) => [...p, newBlock("text")])} />
                <AddBtn testid="add-image" icon={ImageIcon} label="Image" onClick={() => setBlocks((p) => [...p, newBlock("image")])} />
                <AddBtn testid="add-button" icon={CursorClick} label="Button" onClick={() => setBlocks((p) => [...p, newBlock("button")])} />
                <AddBtn testid="add-divider" icon={Minus} label="Divider" onClick={() => setBlocks((p) => [...p, newBlock("divider")])} />
              </div>
            </Panel>
          </aside>

          {/* Preview / canvas */}
          <main className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="w-full min-h-[70vh] py-12 px-4" style={{ background, color: textColor }}>
              <div className={`mx-auto ${widthClass} space-y-6`}>
                {blocks.length === 0 && (
                  <div className="text-center opacity-70 text-sm">Add a block from the left to get started.</div>
                )}
                {blocks.map((b, i) => (
                  <BlockEditor
                    key={b.id}
                    block={b}
                    isFirst={i === 0}
                    isLast={i === blocks.length - 1}
                    textColor={textColor}
                    onChange={(patchObj) => patch(b.id, patchObj)}
                    onRemove={() => removeBlock(b.id)}
                    onMoveUp={() => move(b.id, -1)}
                    onMoveDown={() => move(b.id, 1)}
                  />
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
      <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function AddBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-1 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-100 text-[11px] font-bold py-3 transition-colors"
    >
      <Icon size={18} weight="bold" />
      {label}
    </button>
  );
}

function BlockEditor({ block, isFirst, isLast, textColor, onChange, onRemove, onMoveUp, onMoveDown }) {
  const alignClass = { left: "text-left", center: "text-center", right: "text-right" }[block.align] || "text-center";
  const HEADING_SIZES = { sm: "text-xl", md: "text-2xl", lg: "text-4xl", xl: "text-5xl" };
  const TEXT_SIZES = { sm: "text-xs", md: "text-base", lg: "text-lg", xl: "text-xl" };
  const BTN_SIZES = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-5 py-2.5 text-base", xl: "px-6 py-3 text-lg" };

  const onImgFile = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/uploads/post-image", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ image_url: data.url });
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="group relative rounded-xl outline outline-2 outline-transparent hover:outline-blue-500/70 transition-colors">
      {/* Floating toolbar */}
      <div className="absolute -top-3 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-full shadow px-1 py-0.5 z-10">
        <ToolIconBtn testid={`block-up-${block.id}`} icon={ArrowUp} disabled={isFirst} onClick={onMoveUp} title="Move up" />
        <ToolIconBtn testid={`block-down-${block.id}`} icon={ArrowDown} disabled={isLast} onClick={onMoveDown} title="Move down" />
        <ToolIconBtn testid={`block-remove-${block.id}`} icon={Trash} onClick={onRemove} title="Remove" danger />
      </div>

      <div className={`p-2 ${alignClass}`}>
        {block.type === "heading" && (
          <input
            data-testid={`block-heading-${block.id}`}
            value={block.content}
            onChange={(e) => onChange({ content: e.target.value })}
            className={`w-full bg-transparent border-0 focus:outline-none font-black leading-tight ${HEADING_SIZES[block.size]} ${alignClass}`}
            style={{ color: textColor }}
            placeholder="Your headline"
          />
        )}
        {block.type === "text" && (
          <textarea
            data-testid={`block-text-${block.id}`}
            value={block.content}
            onChange={(e) => onChange({ content: e.target.value })}
            rows={2}
            className={`w-full bg-transparent border-0 focus:outline-none resize-none ${TEXT_SIZES[block.size]} ${alignClass}`}
            style={{ color: textColor }}
            placeholder="Write your paragraph…"
          />
        )}
        {block.type === "image" && (
          <div>
            {block.image_url ? (
              <img src={block.image_url.startsWith("/api/") ? block.image_url : block.image_url} alt="" className="mx-auto max-w-full rounded-lg" />
            ) : (
              <label className="inline-flex items-center gap-2 cursor-pointer rounded-xl border-2 border-dashed border-white/60 px-6 py-8 opacity-90 hover:opacity-100">
                <ImageIcon size={20} weight="bold" />
                <span className="text-sm font-bold">Click to upload image</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onImgFile(e.target.files?.[0])} />
              </label>
            )}
            {block.image_url && (
              <div className="mt-2 flex items-center justify-center gap-2 text-xs">
                <label className="inline-flex items-center gap-1 cursor-pointer underline opacity-90 hover:opacity-100">
                  Replace
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onImgFile(e.target.files?.[0])} />
                </label>
              </div>
            )}
          </div>
        )}
        {block.type === "button" && (
          <button
            type="button"
            className={`inline-flex items-center rounded-full font-black tracking-wide shadow-lg ${BTN_SIZES[block.size]}`}
            style={{ background: textColor, color: block.type === "button" ? "#111827" : textColor }}
          >
            <input
              data-testid={`block-button-${block.id}`}
              value={block.content}
              onChange={(e) => onChange({ content: e.target.value })}
              className="bg-transparent border-0 focus:outline-none text-center"
              style={{ color: "#111827", width: `${Math.max(8, (block.content || "").length + 2)}ch` }}
              placeholder="Button label"
            />
          </button>
        )}
        {block.type === "divider" && (
          <div className="my-2 flex items-center justify-center">
            <div className="h-px w-24 opacity-40" style={{ background: textColor }} />
          </div>
        )}

        {/* Per-block controls (link, align, size) */}
        {block.type !== "divider" && (
          <div className="mt-2 flex flex-wrap items-center justify-center gap-1 text-xs">
            {(block.type === "heading" || block.type === "text" || block.type === "image" || block.type === "button") && (
              <input
                data-testid={`block-link-${block.id}`}
                value={block.link || ""}
                onChange={(e) => onChange({ link: e.target.value })}
                placeholder={block.type === "button" ? "Where should this button link?" : "Optional link (https://…)"}
                className="w-full max-w-md rounded-md bg-white/10 backdrop-blur border border-white/30 px-2 py-1 text-xs text-white/90 placeholder:text-white/50 focus:outline-none"
                style={{ background: "rgba(0,0,0,0.35)", color: "#fff" }}
              />
            )}
            <div className="flex items-center gap-0.5 bg-black/40 rounded-full px-1 py-0.5 text-white">
              {["left","center","right"].map((a) => (
                <button
                  key={a}
                  onClick={() => onChange({ align: a })}
                  className={`px-1.5 py-0.5 rounded-full text-[10px] uppercase ${block.align === a ? "bg-white text-slate-900" : ""}`}
                  data-testid={`block-align-${a}-${block.id}`}
                >
                  {a[0]}
                </button>
              ))}
            </div>
            {(block.type === "heading" || block.type === "text" || block.type === "button") && (
              <div className="flex items-center gap-0.5 bg-black/40 rounded-full px-1 py-0.5 text-white">
                {["sm","md","lg","xl"].map((s) => (
                  <button
                    key={s}
                    onClick={() => onChange({ size: s })}
                    className={`px-1.5 py-0.5 rounded-full text-[10px] uppercase ${block.size === s ? "bg-white text-slate-900" : ""}`}
                    data-testid={`block-size-${s}-${block.id}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolIconBtn({ icon: Icon, onClick, title, disabled, danger, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`h-6 w-6 rounded-full inline-flex items-center justify-center ${danger ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"} disabled:opacity-30 disabled:pointer-events-none`}
    >
      <Icon size={12} weight="bold" />
    </button>
  );
}
